import { useQuery } from '@tanstack/react-query'
import { consoleQueries, getApiErrorMessage } from '@milliswift/api-client'
import {
  Badge,
  Button,
  Callout,
  DataTable,
  EmptyState,
  PageSkeleton,
  PreviewDataBadge,
  SectionHeader,
  type Column,
} from '@milliswift/ui'
import { Table2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { TableInfo, TableResidency } from '@milliswift/types'
import { useDb } from '../lib/db'

const RESIDENCY_TONE: Record<TableResidency, 'success' | 'warning' | 'neutral'> = {
  hot: 'success',
  warm: 'warning',
  cold: 'neutral',
}

/**
 * Tables list. "Cache" is a residency indicator, describing where the data
 * sits today — not something the user configures.
 */
export default function Tables() {
  const { databaseId } = useDb()
  const navigate = useNavigate()
  const tables = useQuery({
    ...consoleQueries.tables(databaseId ?? ''),
    enabled: Boolean(databaseId),
  })

  const columns: Column<TableInfo>[] = [
    {
      key: 'name',
      header: 'Table',
      sortValue: (t) => t.table_name,
      cell: (t) => <span className="font-mono font-medium text-foreground">{t.table_name}</span>,
    },
    {
      key: 'rows',
      header: 'Rows',
      align: 'right',
      sortValue: (t) => t.row_count,
      cell: (t) => <span className="font-mono">{t.row_count.toLocaleString()}</span>,
    },
    {
      key: 'size',
      header: 'Size',
      align: 'right',
      sortValue: (t) => t.size_bytes,
      cell: (t) => (
        <span className="font-mono text-muted">
          {t.size_bytes >= 1e9 ? `${(t.size_bytes / 1e9).toFixed(1)} GB` : `${(t.size_bytes / 1e6).toFixed(1)} MB`}
        </span>
      ),
    },
    {
      key: 'columns',
      header: 'Columns',
      align: 'right',
      sortValue: (t) => t.column_count,
      cell: (t) => <span className="font-mono">{t.column_count}</span>,
    },
    {
      key: 'residency',
      header: 'Cache',
      cell: (t) => (
        <Badge tone={RESIDENCY_TONE[t.residency]} dot>
          {t.residency}
        </Badge>
      ),
    },
    {
      key: 'last',
      header: 'Last queried',
      sortValue: (t) => t.last_query_at ?? '',
      cell: (t) => (t.last_query_at ? new Date(t.last_query_at).toLocaleString() : 'never'),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (t) => (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigate(`/tables/${encodeURIComponent(t.table_name)}`)}
        >
          Explore
        </Button>
      ),
    },
  ]

  if (tables.isPending) return <PageSkeleton />

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Tables"
        description="Every table in the active database."
        action={<PreviewDataBadge />}
      />

      {!databaseId ? (
        <Callout tone="info">Select a database in the header first.</Callout>
      ) : tables.isError ? (
        <Callout tone="danger" title="Could not load tables">
          {getApiErrorMessage(tables.error)}
        </Callout>
      ) : (tables.data ?? []).length === 0 ? (
        <EmptyState
          icon={<Table2 />}
          title="No tables"
          description="Create one from the query editor with a CREATE TABLE statement."
          action={
            <Button size="sm" onClick={() => (window.location.href = '/query')}>
              Open the editor
            </Button>
          }
        />
      ) : (
        <>
          <DataTable
            data={tables.data ?? []}
            columns={columns}
            getRowId={(t) => t.table_name}
            pageSize={20}
            cardTitle={(t) => t.table_name}
          />
          <p className="text-caption text-subtle">
            Cache shows where data sits today: hot is fully cached, cold loads on next query.
          </p>
        </>
      )}
    </div>
  )
}
